<script lang='ts'>
  import { onMount } from 'svelte'

  interface Issue {
    text: string
    count: string
  }

  interface Props {
    title: string
    dashboardState?: any
    issues?: Issue[]
  }

  let {
    title = 'Top 5 issues',
    dashboardState,   
    issues
  }: Props = $props()

  // Use issues from dashboardState if available, otherwise use direct issues prop
  const topIssues = $derived(dashboardState?.topIssues || issues || [])
  const loading = $derived(dashboardState?.topIssuesLoading ?? false)

  // Animated counters: map of index -> displayed value
  let displayedCounts = $state<number[]>([])

  function animateCount(target: number, index: number, duration = 800) {
    const start = performance.now()
    function step(now: number) {
      const progress = Math.min((now - start) / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      displayedCounts[index] = Math.round(eased * target)
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }

  $effect(() => {
    if (!loading && topIssues.length > 0) {
      displayedCounts = new Array(topIssues.length).fill(0)
      topIssues.forEach((issue, i) => {
        const target = parseInt(issue.count, 10) || 0
        animateCount(target, i, 900)
      })
    }
  })
  
  // Fix the state warning by using $effect instead of direct console.log
/*   $effect(() => {
    console.log('TopIssues received dashboardState:', dashboardState);
    console.log('TopIssues received issues:', issues);
    console.log('TopIssues computed topIssues:', topIssues);
  }); */
</script>

<div class="web-top-issues">
  <h3>{title}</h3>
  
  {#if loading}
    <ol class="issues issues--loading" aria-busy="true" aria-label="Loading issues">
      {#each Array(5) as _}
        <li class="top-issue-item">
          <div class="title"><span class="skeleton skeleton--text"></span></div>
          <div class="value"><span class="skeleton skeleton--value"></span></div>
        </li>
      {/each}
    </ol>
  {:else}
    <ol class="issues">
      {#each topIssues as issue, i}
        <li class="top-issue-item">
          <div class="title">
            <span>{issue.text}</span>
          </div>
          <div class="value">{displayedCounts[i] ?? 0}</div>
        </li>
      {/each}
    </ol>
  {/if}
</div>

<style lang="scss">
  .web-top-issues {

    h3 {
      margin-bottom: 1rem;
    }

    .skeleton {
      display: block;
      background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
      border-radius: 4px;

      &--text {
        height: 1rem;
        width: 80%;
      }

      &--value {
        height: 1rem;
        width: 2.5rem;
      }
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    .issues {
      list-style: none;
      padding: 0;
      margin-bottom: 3rem;

      .top-issue-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        border-radius: .8rem;

        &:nth-child(odd) {
          background-color: #e0e0e0;
        }

        .title {
          font-weight: 600;
          flex: 1 1 auto;
          font-size: 2.2rem;
        }
        .value {
          color: var(--uc-red);
          justify-self: flex-end;
          flex: 0 0 auto;
          font-weight: 600;
          font-size: 2.8rem;

        }
      }
    }
  }
  </style>